import cn from 'clsx';

import { makeStyles } from '@material-ui/styles';
import { Modal } from '@material-ui/core';
import { CloseFill, CupLine, Text, UserLine, IconButton } from '@bizzabo/design-system';
import { useEvent } from '@bizzabo/entities';

import { useUserSettings } from '../../settingsNav';
import UserProfile from '../UserProfile';
import { SettingsPage } from './atom';
import SchedulingPreferencesForm from './SchedulingPreferencesForm';
import useTypeTracking from '../../analytics';
import { SETTINGS_BUTTON_TYPE as PROFILE_SETTINGS_BUTTON_TYPE } from '../UserProfile/hooks/useProfileAnalytics';
import useScheduleMeetingsEnabled from '../../meetings/hooks/useScheduleMeetingsEnabled';

const UserSettings: React.FC<{ eventUniqueName: string }> = ({ eventUniqueName }) => {
    const {
        userModalStatus,
        currentView,
        closeUserSettings,
        openScheduledMeetings,
        openUserProfile,
    } = useUserSettings();
    const { trackClickEvent } = useTypeTracking();

    const classes = useStyles();

    const { data: isNetworkingSettingEnabled } = useScheduleMeetingsEnabled(eventUniqueName);
    const { data: event } = useEvent(eventUniqueName);

    const closeModal = () => {
        if (currentView === SettingsPage.SchedulingPreferences) {
            trackClickEvent({
                category: 'MEETINGS_SETTINGS',
                subCategory: 'CLOSE_MEETINGS_SETTINGS',
            });
        } else if (currentView === SettingsPage.ProfilePreferences) {
            trackClickEvent({
                category: PROFILE_SETTINGS_BUTTON_TYPE,
                subCategory: 'CLOSE_PROFILE_SETTINGS',
            });
        }
        closeUserSettings();
    };

    const controlClose = (e: MouseEvent) => {
        // here we can control closing modal popup in case if form is dirty
        // and we can as a user about approval disregard their changes
        e.preventDefault();
        e.stopPropagation();
        closeModal();
    };

    return (
        <Modal open={userModalStatus} onClose={controlClose}>
            <div className={classes.modal}>
                <div className={classes.sidebar}>
                    <Text variant="t40" className={classes.sidebarHeader}>
                        Settings
                    </Text>
                    <button
                        className={cn(
                            classes.pageLink,
                            currentView === SettingsPage.ProfilePreferences
                                ? classes.activePageLink
                                : classes.inactivePageLink
                        )}
                        onClick={openUserProfile}
                    >
                        <UserLine />
                        Profile
                    </button>
                    <hr className={classes.sidebarDivider} />
                    {isNetworkingSettingEnabled && (
                        <button
                            className={cn(
                                classes.pageLink,
                                currentView === SettingsPage.SchedulingPreferences
                                    ? classes.activePageLink
                                    : classes.inactivePageLink
                            )}
                            onClick={openScheduledMeetings}
                        >
                            <CupLine className={classes.cupIcon} />
                            Meetings
                        </button>
                    )}
                </div>
                <div className={classes.main}>
                    <div className={classes.closeHeader}>
                        <div className={classes.closeSection}>
                            <IconButton
                                onClick={closeModal}
                                icon={CloseFill}
                                className={classes.closeBtn}
                            />
                        </div>
                    </div>
                    <div className={classes.mainContent}>
                        {currentView === SettingsPage.ProfilePreferences ? (
                            <UserProfile modalView />
                        ) : null}
                        {isNetworkingSettingEnabled &&
                        currentView === SettingsPage.SchedulingPreferences ? (
                            <SchedulingPreferencesForm eventId={event?.id} />
                        ) : null}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export const useStyles = makeStyles(
    theme => ({
        modal: {
            outline: 'none', // prevents :focus-visible  border
            display: 'flex',
            flexDirection: 'row',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '90%',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
            borderRadius: 33,
            backgroundColor: theme.palette.greys[100],
            transform: `translate(-50%, -50%)`,
            [theme.breakpoints.up('sm')]: {
                maxWidth: 810,
                height: '80vh',
            },
        },
        sidebar: {
            borderTopLeftRadius: 33,
            borderBottomLeftRadius: 33,
            width: 250,
            paddingTop: 40,
            paddingLeft: 30,
            paddingRight: 30,
            backgroundColor: theme.palette.greys[200],
        },
        main: {
            flex: '2 0px',
            borderTopRightRadius: 33,
            borderBottomRightRadius: 33,
            position: 'relative',
        },
        mainContent: {
            paddingTop: 24,
            position: 'relative',
            height: 'calc(100% - 80px)', // 80px - '.closeHeader' minHeight
            '&::-webkit-scrollbar': {
                height: '100%',
            },
        },
        sidebarHeader: {
            color: theme.palette.greys[400],
            marginBottom: 36,
        },
        sidebarDivider: {
            backgroundColor: theme.palette.greys[400],
            height: 1,
            border: 0,
            marginTop: 8,
            marginBottom: 8,
        },
        activePageLink: {
            color: theme.palette.greys[700],
        },
        inactivePageLink: {
            color: theme.palette.greys[500],
        },
        pageLink: {
            ...theme.typography.t70,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '6px 4px',
            cursor: 'pointer',
            border: 0,
            '&:hover': {
                border: 0,
            },
            background: 'transparent',
            '& svg': {
                marginRight: 10,
                width: 15,
                height: 15,
            },
        },
        cupIcon: {
            marginTop: 2,
        },
        closeHeader: {
            width: '100%',
            minHeight: 72,
            display: 'flex',
            justifyContent: 'flex-end',
        },
        closeSection: {
            ...theme.typography.t80,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            borderBottom: 'none',
            paddingRight: 16,
            backgroundColor: theme.palette.greys[100],
            borderTopRightRadius: 33,
            '& svg': {
                width: 25,
                height: 25,
            },
        },
        closeBtn: {
            backgroundColor: theme.palette.greys[100],
        },
    }),
    { classNamePrefix: 'people-user-profile-settings' }
);

export default UserSettings;
